import Joi from 'joi';
import PropTypes from 'prop-types';
import { useCallback, useEffect, useState } from 'react';

const columnObjectSchema = Joi.object({
  key: Joi.string().required(),
  label: Joi.string().required(),
  selected: Joi.boolean().required(),
  hide: Joi.boolean(),
});

const localStorageSchema = Joi.array().items(columnObjectSchema);

function useColumnsControl({ id, order }) {
  if (!id) {
    throw new Error('useColumnsControl: "id" is required.');
  }
  if (!order || !Array.isArray(order)) {
    throw new Error('useColumnsControl: "order" must be a valid array.');
  }
  const [localOrder, setLocalOrder] = useState(order);
  const [columnOptions, setColumnOptions] = useState(null);
  const [renderInTableOrder, setRenderInTableOrder] = useState([]);
  const [selectableColumnsOptions, setSelectableColumnsOptions] = useState([]);
  const localStorageKey = `${id}-tableColumns`;

  const saveColumnsToLocalStorage = useCallback(
    columns => {
      localStorage.setItem(localStorageKey, JSON.stringify(columns));
    },
    [localStorageKey],
  );

  function loadColumnsFromLocalStorage() {
    try {
      const storedColumns = localStorage.getItem(localStorageKey);
      if (!storedColumns) return null;

      const parsedColumns = JSON.parse(storedColumns);
      const result = localStorageSchema.validate(parsedColumns);
      if (result.error) return null;

      return parsedColumns;
    } catch (error) {
      console.error('Error loading columns from localStorage:', error);
      return null;
    }
  }

  function getDefaultOptions() {
    const optionItems = localOrder.filter(item => !item.mandatory);
    const preSelectedItems = optionItems.filter(item => item.defaultSelected);
    const remainingItems = optionItems.filter(item => !item.defaultSelected);

    const preSelectedOptions = preSelectedItems.map(orderItem => ({
      key: orderItem.key,
      label: orderItem.label || orderItem.key,
      selected: true,
    }));
    const remainingOptions = remainingItems.map(orderItem => ({
      key: orderItem.key,
      label: orderItem.label || orderItem.key,
      selected: false,
    }));
    return [...preSelectedOptions, ...remainingOptions];
  }

  function getInitialOptions() {
    const localStorageColumnOptions = loadColumnsFromLocalStorage();
    const currentDefaultOptions = getDefaultOptions();
    if (!localStorageColumnOptions) return currentDefaultOptions;

    const missingColumns = currentDefaultOptions.filter(currentOption => {
      return !localStorageColumnOptions.some(storedOption => storedOption.key === currentOption.key);
    });

    const outdatedColumnKeys = localStorageColumnOptions
      .filter(storedOption => {
        return !currentDefaultOptions.some(currentOption => currentOption.key === storedOption.key);
      })
      .map(option => option.key);

    if (!missingColumns.length && !outdatedColumnKeys.length) {
      return localStorageColumnOptions;
    }

    outdatedColumnKeys.forEach(key => {
      const option = localStorageColumnOptions.find(option => option.key === key);
      if (option) {
        option.hide = true;
      }
    });

    const updatedOptions = [...localStorageColumnOptions, ...missingColumns];

    saveColumnsToLocalStorage(updatedOptions);
    return updatedOptions;
  }

  useEffect(() => {
    const initialOptions = getInitialOptions();
    setColumnOptions(initialOptions);
  }, [localOrder]);

  function changeColumnOptionsOrder(oldKey, newKey) {
    setColumnOptions(prevOptions => {
      const options = [...prevOptions];
      const oldIndex = options.findIndex(option => option.key === oldKey);
      const newIndex = options.findIndex(option => option.key === newKey);
      // Take out the item at oldIndex
      const movedItem = options.splice(oldIndex, 1)[0];
      // Insert the item after newIndex
      options.splice(newIndex, 0, movedItem);
      return options;
    });
  }

  function onOptionClick(optionKey) {
    setColumnOptions(prevOptions => {
      const updatedOptions = prevOptions.map(option => {
        if (option.key === optionKey) {
          return { ...option, selected: !option.selected };
        }
        return option;
      });
      return updatedOptions;
    });
  }

  function selectAllColumns() {
    const allSelectedOptions = columnOptions.map(option => ({
      ...option,
      selected: true,
    }));
    setColumnOptions(allSelectedOptions);
  }

  function deselectAllColumns() {
    const allDeselectedOptions = columnOptions.map(option => ({
      ...option,
      selected: false,
    }));
    setColumnOptions(allDeselectedOptions);
  }

  useEffect(() => {
    if (!columnOptions) return;
    saveColumnsToLocalStorage(columnOptions);
  }, [columnOptions, saveColumnsToLocalStorage]);

  function resetToDefault() {
    setColumnOptions(getDefaultOptions());
  }

  useEffect(() => {
    // If columnOptions have not been initialized yet, do nothing
    if (!columnOptions) return;
    // If columnOptions is empty, clear everything
    if (columnOptions.length === 0) {
      setRenderInTableOrder([]);
      setSelectableColumnsOptions([]);
      return;
    }

    const filteredLocalOrder = localOrder.filter(item => !item.hide);
    const filteredLocalOrderKeys = filteredLocalOrder.map(item => item.key);
    // Final list of column options that are allowed to be shown. If empty, nothing can be shown.
    const allowedColumnOptions = columnOptions?.filter(option => filteredLocalOrderKeys.includes(option.key));
    if (allowedColumnOptions.length === 0) {
      setRenderInTableOrder([]);
      setSelectableColumnsOptions([]);
      return;
    }

    // Find and set all options that is optional if new list differs.
    const optionalColumnOptions = allowedColumnOptions.filter(option => !option.mandatory);
    if (JSON.stringify(optionalColumnOptions) !== JSON.stringify(selectableColumnsOptions)) {
      setSelectableColumnsOptions(optionalColumnOptions);
    }

    // Determine which columns should be rendered as columns in the table. Set if list differs.
    const mandatoryColumnsOrder = filteredLocalOrder.filter(item => item.mandatory);
    const optionalColumnsOrder = filteredLocalOrder.filter(item => !item.mandatory);
    const selectedOptionalColumnKeys = optionalColumnOptions
      .filter(option => option.selected)
      .map(option => option.key);
    const selectedOptionalColumnsOrder = selectedOptionalColumnKeys
      .map(key => optionalColumnsOrder.find(item => item.key === key))
      .filter(Boolean);
    const newRenderInTableOrder = [...mandatoryColumnsOrder, ...selectedOptionalColumnsOrder];
    if (JSON.stringify(newRenderInTableOrder) !== JSON.stringify(renderInTableOrder)) {
      setRenderInTableOrder(newRenderInTableOrder);
    }
  }, [columnOptions, localOrder, selectableColumnsOptions, renderInTableOrder]);

  const mandatoryColumnKeys = localOrder.filter(item => item.mandatory && item.key).map(item => item.key);

  return {
    columnOptions,
    onOptionClick,
    selectAllColumns,
    deselectAllColumns,
    changeColumnOptionsOrder,
    resetToDefault,
    selectableColumnsOptions,
    renderInTableOrder,
    mandatoryColumnKeys,
    setOrder: setLocalOrder,
  };
}

const optionShape = PropTypes.shape({
  key: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  selected: PropTypes.bool.isRequired,
});

export const columnsControlShape = {
  columnOptions: PropTypes.arrayOf(optionShape).isRequired,
  onOptionClick: PropTypes.func.isRequired,
  selectAllColumns: PropTypes.func.isRequired,
  deselectAllColumns: PropTypes.func.isRequired,
  changeColumnOptionsOrder: PropTypes.func.isRequired,
  resetToDefault: PropTypes.func.isRequired,
  selectableColumnsOptions: PropTypes.arrayOf(optionShape).isRequired,
  renderInTableOrder: PropTypes.arrayOf(optionShape).isRequired,
  mandatoryColumnKeys: PropTypes.arrayOf(PropTypes.string).isRequired,
  setOrder: PropTypes.func.isRequired,
};

export default useColumnsControl;
